// ─────────────────────────────────────────────
//  useTheme.js
//  src/composables/useTheme.js
//
//  Usage in any component:
//    import { useTheme } from '@/composables/useTheme'
//    const { theme, toggleTheme, setTheme } = useTheme()
//
//  Theme values: 'dark' | 'light' | 'system'
// ─────────────────────────────────────────────

import { ref, watchEffect } from 'vue'

const STORAGE_KEY = 'samt:theme'

// Shared state — one instance across all components
const theme = ref(localStorage.getItem(STORAGE_KEY) || 'system')

function applyTheme(value) {
  const html = document.documentElement

  if (value === 'system') {
    // Remove attribute — CSS media query takes over
    html.removeAttribute('data-theme')
  } else {
    html.setAttribute('data-theme', value)
  }
}

// Apply on change
watchEffect(() => {
  applyTheme(theme.value)
  localStorage.setItem(STORAGE_KEY, theme.value)
})

// Listen for system preference changes when in 'system' mode
if (window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (theme.value === 'system') {
        // Force a re-apply so reactive watchers notice
        applyTheme('system')
      }
    })
}

export function useTheme() {
  function setTheme(value) {
    if (['dark', 'light', 'system'].includes(value)) {
      theme.value = value
    }
  }

  function toggleTheme() {
    // Cycles: dark → light → dark
    // (system is only set explicitly, not via toggle)
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
  }

  // Resolved theme — what the user actually sees
  // ('system' resolves to 'dark' or 'light' based on OS)
  function resolvedTheme() {
    if (theme.value !== 'system') return theme.value
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }

  return {
    theme,        // 'dark' | 'light' | 'system'
    setTheme,
    toggleTheme,
    resolvedTheme,
  }
}
